import type {
  CompanyCandidatesResponse,
  CreateDiscoveryRequest,
  CreateDiscoveryResponse,
  Discovery,
  DiscoveryStatusResponse,
  DiscoveryView,
  ImportDiscoveryLinkResponse,
  ListDiscoveriesResponse,
  StartMeetingFromDiscoveryResponse,
  UpdateDiscoveryRequest,
} from "@scoach/types";

import { api } from "../../lib/http.ts";

/** Deep-research "Discovery" workspace API (company prep). */
export const discoveryApi = {
  /** Disambiguation: companies matching a typed name (logo + domain + one-liner). */
  candidates: (name: string) =>
    api<CompanyCandidatesResponse>("/companies/candidates", { query: { name } }).then(
      (r) => r.candidates,
    ),

  create: (body: CreateDiscoveryRequest) =>
    api<CreateDiscoveryResponse>("/discoveries", { method: "POST", body }),

  status: (id: string) => api<DiscoveryStatusResponse>(`/discoveries/${id}/status`),

  get: (id: string) => api<DiscoveryView>(`/discoveries/${id}`),

  list: () => api<ListDiscoveriesResponse>("/discoveries").then((r) => r.items),

  patch: (id: string, body: UpdateDiscoveryRequest) =>
    api<Discovery>(`/discoveries/${id}`, { method: "PATCH", body }),

  remove: (id: string) => api<{ ok: boolean }>(`/discoveries/${id}`, { method: "DELETE" }),

  uploadAttachments: (id: string, files: File[]) => {
    const fd = new FormData();
    for (const f of files) fd.append("file", f);
    return api<{ attachments: Discovery["attachments"] }>(`/discoveries/${id}/attachments`, {
      method: "POST",
      body: fd,
    });
  },

  /** Import a context doc from a Google Drive/Docs link (token → X-Drive-Token). */
  importAttachmentLink: (id: string, url: string, googleToken?: string) =>
    api<ImportDiscoveryLinkResponse>(`/discoveries/${id}/attachments/link`, {
      method: "POST",
      body: { url },
      headers: googleToken ? { "X-Drive-Token": googleToken } : undefined,
    }),

  removeAttachment: (id: string, fileId: string) =>
    api<{ attachments: Discovery["attachments"] }>(`/discoveries/${id}/attachments/${fileId}`, {
      method: "DELETE",
    }),

  startMeeting: (id: string, mode: "live" | "setup") =>
    api<StartMeetingFromDiscoveryResponse>(`/discoveries/${id}/start-meeting`, {
      method: "POST",
      body: { mode },
    }),
};
